import React, { useEffect } from 'react';
import { Award, Users, Globe, TrendingUp } from 'lucide-react';
import { timeline } from '../data/mock';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: Award, number: '50M+', label: 'Carbon Credits' },
    { icon: TrendingUp, number: '250+', label: 'Clients' },
    { icon: Globe, number: '400+', label: 'Active Projects' },
    { icon: Users, number: '15+', label: 'Years' }
  ];

  return (
    <div className="about-page bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#FAFAFA] border-b border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            <div className="accent-line mb-6" />
            <span className="overline">Our Story</span>
            <h1 className="text-[#09090B] mt-4 mb-6">From Vision to Leadership</h1>
            <p className="text-lg md:text-xl text-[#52525B] leading-relaxed">
              Pioneering India's climate action movement since 2012, empowering businesses and governments to become active participants in global sustainability.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-zinc-200">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="p-8 md:p-10 border-l-2 border-[#047857]" data-testid={`about-stat-${index}`}>
                <Icon className="w-8 h-8 text-[#047857] mb-4" />
                <div className="font-display text-3xl md:text-4xl font-extrabold text-[#09090B] mb-1">{stat.number}</div>
                <div className="text-sm text-[#52525B] uppercase tracking-wider">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B] mb-8">The Creduce Story</h2>
            <div className="space-y-6 text-black leading-relaxed text-lg">
              <p>
                <strong className="text-black text-xl">CREDUCE</strong> — born from the fusion of <strong>Carbon</strong> and <strong>Reduce</strong> — embodies a single, unwavering philosophy: to heal the planet by eliminating, offsetting, and mitigating carbon emissions at scale.
              </p>
              <p>
                Our story began in <strong>2012</strong>, when <strong>Shailendra Singh Rao</strong>, a visionary driven by an unshakable belief in sustainable transformation, founded <strong>Envarrior</strong> — a name that echoed the spirit of <em>"Environmental Warriors."</em> It was a time when the carbon market was still taking shape in India, when climate consciousness was a whisper rather than a movement. Yet, against the odds, Envarrior began to plant the seeds of a cleaner, more responsible tomorrow.
              </p>
              <p>
                As the years unfolded, our purpose evolved — from being climate consultants to becoming architects of a carbon-neutral future. In <strong>2021</strong>, we stepped into a new era with a new identity — <strong>Creduce Technologies Private Limited</strong> — carrying forward the same mission, but with renewed energy, deeper intent, and a global vision: to <strong>digitize the global carbon market</strong> and make climate action accessible, transparent, and rewarding for everyone.
              </p>
              <p>
                Headquartered in <strong>Ahmedabad, Gujarat</strong>, Creduce today stands as a symbol of trust and innovation in the climate space — recognized among India's most respected names in <strong>Climate Change Mitigation</strong>, <strong>Carbon Credit Development</strong>, <strong>I-REC Trading</strong>, and <strong>Sustainability Consulting</strong>.
              </p>
              <p>
                Our impact resonates far beyond borders. With a global footprint spanning <strong>13+ regions</strong>, a network of strategic partners, and a growing community of <strong>250+ clients</strong>, Creduce is redefining how the world measures, manages, and monetizes carbon responsibility. From renewable energy developers and industries to investors and policymakers, we are enabling every stakeholder to become an active participant in the <strong>low-carbon revolution</strong>.
              </p>
              <p className="italic text-black text-xl font-semibold">
                But Creduce is more than a company — it is a movement.
              </p>
              <p>
                A collective of dreamers, scientists, strategists, and changemakers — united by the belief that the Earth deserves better and that technology, when guided by conscience, can heal the damage of centuries.
              </p>
              <p>
                As we stride toward a <strong>net-zero future</strong>, we continue to expand our horizon — building digital infrastructure for carbon markets, pioneering GHG reduction methodologies, designing innovative sustainability frameworks, and empowering communities with climate literacy.
              </p>
              <p>
                Every ton of carbon reduced, every project registered, every certificate traded tells a story — a story of hope, restoration, and resilience.
              </p>
              <p className="text-black font-semibold text-lg">
                And through it all, our purpose remains timeless and clear:<br />
                <em>To create a world where sustainability is not an obligation — but a way of life.</em>
              </p>
              <p className="pt-8 border-t border-zinc-200">
                <span className="text-2xl font-bold text-black">Creduce</span> — <span className="text-xl font-semibold text-black">Carbon Reduced. Planet Restored.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y border-zinc-200">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-px bg-zinc-200 max-w-5xl mx-auto border border-zinc-200">
            <div className="bg-white p-10">
              <div className="w-14 h-14 bg-[#047857]/10 flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-[#047857]" />
              </div>
              <h3 className="text-2xl font-bold text-[#09090B] mb-4">Our Vision</h3>
              <p className="text-[#52525B] leading-relaxed">
                To digitize the global carbon market and make climate action accessible, transparent, and rewarding for individuals, corporations, and governments worldwide.
              </p>
            </div>
            <div className="bg-white p-10">
              <div className="w-14 h-14 bg-[#047857]/10 flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-[#047857]" />
              </div>
              <h3 className="text-2xl font-bold text-[#09090B] mb-4">Our Mission</h3>
              <p className="text-[#52525B] leading-relaxed">
                To empower businesses, governments, and individuals with sustainable solutions that are measurable, verifiable, and impactful — accelerating the world's shift to a Low-Carbon Economy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B] mb-4">Our Journey</h2>
            <p className="text-lg text-[#52525B]">
              From a startup vision to industry leadership
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-0">
              {timeline.map((item, index) => (
                <div key={item.year} className="flex gap-6 md:gap-10">
                  <div className="flex-shrink-0 w-16 md:w-24 text-right pt-1">
                    <div className="font-display text-xl md:text-2xl font-extrabold text-[#047857]">{item.year}</div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-center pt-2">
                    <div className="w-3 h-3 bg-[#047857]" />
                    {index < timeline.length - 1 && <div className="w-px flex-1 bg-zinc-200 mt-1" />}
                  </div>
                  <div className="flex-1 pb-10">
                    <h3 className="text-lg font-bold text-[#09090B] mb-2">{item.title}</h3>
                    <p className="text-[#52525B]">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
